import { useEffect, useState, useContext } from 'react';
import Layout from '@/components/Layout';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Clock, Award, Lock, CheckCircle, TrendingUp, DollarSign } from 'lucide-react';
import { AuthContext } from '@/App';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import EditableText from '@/components/EditableText';
import PageQuote from '@/components/PageQuote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TestingCenterPage() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState({});
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(true);
  const [heroText, setHeroText] = useState({
    title: 'Testing Center',
    subtitle: 'Prepare for Your Acupuncture Boards',
    description: 'Three Levels of Practice Testing'
  });
  const heroImage = useHeroImage('testing_center');

  useEffect(() => {
    loadHeroText();
    loadPricing();
    if (user) {
      checkSubscriptions();
    }
    
    if (searchParams.get('purchase') === 'success') {
      toast.success('Purchase successful! You now have access.');
      checkSubscriptions();
    }
  }, [user]);

  const loadPricing = async () => {
    try {
      // Load pricing for all test tiers
      const [basicRes, compsRes, advancedRes] = await Promise.allSettled([
        axios.get(`${API}/pricing/test_tier/basic`),
        axios.get(`${API}/pricing/test_tier/comps_ready`),
        axios.get(`${API}/pricing/test_tier/advanced`)
      ]);
      
      setPricing({
        basic: basicRes.status === 'fulfilled' ? basicRes.value.data : null,
        comps_ready: compsRes.status === 'fulfilled' ? compsRes.value.data : null,
        advanced: advancedRes.status === 'fulfilled' ? advancedRes.value.data : null
      });
    } catch (error) {
      console.error('Failed to load pricing:', error);
    }
  };

  const loadHeroText = async () => {
    try {
      const response = await axios.get(`${API}/page-settings`);
      const testingCenterSettings = response.data.find(p => p.page_name === 'testing_center');
      if (testingCenterSettings) {
        setHeroText({
          title: testingCenterSettings.hero_title || 'Testing Center',
          subtitle: testingCenterSettings.hero_subtitle || 'Prepare for Your Acupuncture Boards',
          description: testingCenterSettings.hero_description || 'Three Levels of Practice Testing'
        });
      }
    } catch (error) {
      console.error('Failed to load hero text:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscriptions = async () => {
    try {
      // For now, check if user has any test subscriptions
      // We'll create a public endpoint to check user's own subscriptions
      const response = await axios.get(`${API}/my-test-access`, { withCredentials: true });
      
      setSubscriptions({
        basic: response.data.basic_access || false,
        comps_ready: response.data.comps_ready_access || false,
        advanced: response.data.advanced_access || false
      });
    } catch (error) {
      console.error('Failed to check subscriptions:', error);
      // Default: no access on error
      setSubscriptions({
        basic: false,
        comps_ready: false,
        advanced: false
      });
    }
  };

  const handleTierClick = async (tier) => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please login to access tests');
      handleLogin();
      return;
    }

    // Check if user has access to this tier
    const hasAccess = subscriptions[tier];
    
    if (hasAccess) {
      // User has access - navigate to tier page
      const tierPath = tier === 'comps_ready' ? 'comps-ready' : tier;
      window.location.href = `/testing-center/${tierPath}`;
    } else {
      // User doesn't have access - initiate purchase
      await handlePurchase(tier);
    }
  };

  const handlePurchase = async (tier) => {
    // Get pricing for this tier
    const tierPricing = pricing[tier];
    
    if (!tierPricing) {
      toast.error('Pricing not configured for this tier yet. Please check back later or contact support.');
      return;
    }

    // Check if this tier is free
    if (tierPricing.is_free) {
      toast.info('Granting free access...');
      
      try {
        // Create checkout session (backend will grant access without payment)
        const response = await axios.post(
          `${API}/payments/checkout/session`,
          {
            item_type: 'test_tier',
            item_id: tier,
            amount: tierPricing.amount,
            currency: tierPricing.currency,
            description: tierPricing.description,
            success_url: `${window.location.origin}/testing-center?purchase=success`,
            cancel_url: `${window.location.origin}/testing-center?purchase=cancelled`
          },
          { withCredentials: true }
        );

        if (response.data.is_free) {
          // Free tier - access granted directly
          toast.success('Access granted! Redirecting...');
          
          // Refresh subscriptions
          await checkSubscriptions();
          
          // Navigate to tier page
          setTimeout(() => {
            const tierPath = tier === 'comps_ready' ? 'comps-ready' : tier;
            window.location.href = `/testing-center/${tierPath}`;
          }, 1000);
          return;
        }
      } catch (error) {
        console.error('Free tier access error:', error);
        toast.error('Failed to grant free access. Please try again.');
        return;
      }
    }

    toast.info('Redirecting to checkout...');
    
    try {
      // Create checkout session
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          item_type: 'test_tier',
          item_id: tier,
          amount: tierPricing.amount,
          currency: tierPricing.currency,
          description: tierPricing.description,
          success_url: `${window.location.origin}/testing-center?purchase=success`,
          cancel_url: `${window.location.origin}/testing-center?purchase=cancelled`
        },
        { withCredentials: true }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Purchase error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to start checkout';
      toast.error(`Checkout error: ${errorMsg}`);
    }
  };

  const handleLogin = () => {
    // This will trigger the login modal from Layout component
    toast.info('Please log in to access all tests');
  };

  return (
    <Layout>
      <section className="min-h-screen" data-testid="testing-center-page">
        {/* Hero */}
        <div className="relative h-[400px] overflow-hidden">
          <HeroImageEditor pageName="testing_center" />
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: 'sepia(45%) saturate(115%) hue-rotate(-5deg) brightness(0.7)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#2C1810]/70 via-[#E8934D]/20 to-[#2C1810]/90"></div>
          
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-6xl retro-title mb-4 text-center">{heroText.title}</h1>
            <p className="text-2xl text-[#F4E4C1] font-bold mb-2 text-center">{heroText.subtitle}</p>
            <p className="text-lg text-[#E8934D] font-bold text-center">{heroText.description}</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Quote Position 1 - Above Main Content */}
          <PageQuote pageName="testing_center" position={1} variant="compact" />

          {/* Access Banner */}
          <div className="cassette-card p-8 mb-12 text-center border-4 border-[#E8934D]">
            <Award className="w-20 h-20 text-[#E8934D] mx-auto mb-4" />
            {user ? (
              <>
                <h2 className="text-4xl retro-title text-[#2C1810] mb-4">
                  <EditableText as="span" id="testing_center.status.title">Your Access Status</EditableText>
                </h2>
                <p className="text-xl text-[#6B4423] mb-4">Welcome back, {user.name}!</p>
                <div className="flex flex-wrap justify-center gap-3 mb-4">
                  {subscriptions.basic && (
                    <span className="px-4 py-2 bg-[#4D9E7C] text-white rounded-lg font-bold flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Basic Practice
                    </span>
                  )}
                  {subscriptions.comps_ready && (
                    <span className="px-4 py-2 bg-[#E8934D] text-white rounded-lg font-bold flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Comps Ready
                    </span>
                  )}
                  {subscriptions.advanced && (
                    <span className="px-4 py-2 bg-[#E85D4D] text-white rounded-lg font-bold flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Advanced
                    </span>
                  )}
                </div>
                {subscriptions.basic && subscriptions.comps_ready && subscriptions.advanced ? (
                  <p className="text-lg text-[#4D9E7C] font-bold">🎉 Full Access - All Tiers Unlocked!</p>
                ) : (
                  <p className="text-sm text-[#6B4423]">Unlock more tiers to expand your practice</p>
                )}
              </>
            ) : (
              <>
                <h2 className="text-4xl retro-title text-[#2C1810] mb-4">
                  <EditableText as="span" id="testing_center.cta.title">Start Your Journey</EditableText>
                </h2>
                <p className="text-xl text-[#6B4423] mb-4">
                  <EditableText as="span" id="testing_center.cta.subtitle">Create a free account to access all tests</EditableText>
                </p>
                <button onClick={handleLogin} className="retro-button w-full sm:w-auto">
                  <EditableText as="span" id="testing_center.cta.button">Sign Up / Login</EditableText>
                </button>
              </>
            )}
          </div>

          {/* Features Grid */}
          {user && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <button 
                onClick={() => handleTierClick('basic')}
                className="cassette-card p-6 text-center cursor-pointer hover:shadow-xl transition-shadow relative"
              >
                {subscriptions.basic && (
                  <div className="absolute top-4 right-4">
                    <CheckCircle className="w-6 h-6 text-[#4D9E7C]" />
                  </div>
                )}
                <BookOpen className="w-12 h-12 text-[#4D9E7C] mx-auto mb-3" />
                <h3 className="text-xl retro-title text-[#2C1810] mb-2">Basic Practice</h3>
                <p className="text-sm text-[#6B4423] mb-3">10-question quizzes, no time limit, unlimited attempts</p>
                {pricing.basic ? (
                  <div className="flex flex-col items-center gap-2 mt-4 pt-4 border-t-2 border-[#E8934D]">
                    {pricing.basic.amount === 0 ? (
                      <span className="text-2xl font-bold text-[#4D9E7C]">FREE</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-white" />
                        <span className="text-2xl font-bold text-white">${pricing.basic.amount.toFixed(2)}</span>
                      </div>
                    )}
                    {!subscriptions.basic && (
                      <span className="text-xs text-[#2C1810] font-bold">
                        {pricing.basic.amount === 0 ? 'Click to Access' : 'Click to Purchase'}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t-2 border-[#E8934D]">
                    <span className="text-sm text-[#6B4423]">Contact for pricing</span>
                  </div>
                )}
              </button>
              <button 
                onClick={() => handleTierClick('comps_ready')}
                className="cassette-card p-6 text-center cursor-pointer hover:shadow-xl transition-shadow relative"
              >
                {subscriptions.comps_ready && (
                  <div className="absolute top-4 right-4">
                    <CheckCircle className="w-6 h-6 text-[#E8934D]" />
                  </div>
                )}
                <Award className="w-12 h-12 text-[#E8934D] mx-auto mb-3" />
                <h3 className="text-xl retro-title text-[#2C1810] mb-2">Comps Ready</h3>
                <p className="text-sm text-[#6B4423] mb-3">25-question timed tests, comprehensive coverage</p>
                {pricing.comps_ready ? (
                  <div className="flex flex-col items-center gap-2 mt-4 pt-4 border-t-2 border-[#E8934D]">
                    {pricing.comps_ready.amount === 0 ? (
                      <span className="text-2xl font-bold text-[#E8934D]">FREE</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-white" />
                        <span className="text-2xl font-bold text-white">${pricing.comps_ready.amount.toFixed(2)}</span>
                      </div>
                    )}
                    {!subscriptions.comps_ready && (
                      <span className="text-xs text-[#2C1810] font-bold">
                        {pricing.comps_ready.amount === 0 ? 'Click to Access' : 'Click to Purchase'}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t-2 border-[#E8934D]">
                    <span className="text-sm text-[#6B4423]">Contact for pricing</span>
                  </div>
                )}
              </button>
              <button 
                onClick={() => handleTierClick('advanced')}
                className="cassette-card p-6 text-center cursor-pointer hover:shadow-xl transition-shadow relative"
              >
                {subscriptions.advanced && (
                  <div className="absolute top-4 right-4">
                    <CheckCircle className="w-6 h-6 text-[#E85D4D]" />
                  </div>
                )}
                <TrendingUp className="w-12 h-12 text-[#E85D4D] mx-auto mb-3" />
                <h3 className="text-xl retro-title text-[#2C1810] mb-2">Advanced</h3>
                <p className="text-sm text-[#6B4423] mb-3">50-75 questions, advanced channels & theory</p>
                {pricing.advanced ? (
                  <div className="flex flex-col items-center gap-2 mt-4 pt-4 border-t-2 border-[#E8934D]">
                    {pricing.advanced.amount === 0 ? (
                      <span className="text-2xl font-bold text-[#E85D4D]">FREE</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-white" />
                        <span className="text-2xl font-bold text-white">${pricing.advanced.amount.toFixed(2)}</span>
                      </div>
                    )}
                    {!subscriptions.advanced && (
                      <span className="text-xs text-[#2C1810] font-bold">
                        {pricing.advanced.amount === 0 ? 'Click to Access' : 'Click to Purchase'}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t-2 border-[#E8934D]">
                    <span className="text-sm text-[#6B4423]">Contact for pricing</span>
                  </div>
                )}
              </button>
            </div>
          )}

        </div>
      </section>

      {/* Quote Position 2 - Below Main Content */}
      <PageQuote pageName="testing_center" position={2} variant="compact" />
    </Layout>
  );
}
